import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { addLabels } from '../shared/add-labels';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';
import { useChartOnReady } from '../shared/use-chart-on-ready';

export type ChartRef = {
  series: any[] | null;
  seriesKeys: string[];
  onReady: (_callback: (_series: any[]) => void) => void;
};

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  xAxisField: string;
  download: boolean;
  yAxisLabel: string | undefined;
  xAxisLabel: string | undefined;
};

const Chart = React.forwardRef<ChartRef, ChartProps>(
  ({ id, data, fullScreen, xAxisField, download, yAxisLabel, xAxisLabel }, ref) => {
    const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
    const theme = mui.styles.useTheme() as mui.core.Theme;
    const seriesKeys = Object.keys(data?.[0] ?? {}).filter((key) => key !== xAxisField);
    const [seriesState, setSeriesState] = React.useState<any[] | null>(null);
    const { registerCallback, executeCallbacks } = useChartOnReady();

    React.useImperativeHandle(
      ref,
      () => ({
        series: seriesState,
        seriesKeys,
        onReady: registerCallback,
      }),
      [seriesState, seriesKeys, registerCallback]
    );

    React.useEffect(() => {
      // Create root element
      const root = am5.Root.new(widgetId);

      // Remove amCharts logo
      root._logo?.dispose();

      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: true,
          layout: root.verticalLayout,
          maxTooltipDistance: 0,
          paddingLeft: getPaddingLeft(download, yAxisLabel),
        })
      );

      // Root settings
      chart.appear(1000, 100);

      // Create elements
      const { xAxis, yAxis } = createAxes({ root, chart, data, xAxisField });
      const createdSeries = createSeries({
        root,
        chart,
        data,
        xAxisField,
        xAxisRenderer: xAxis,
        yAxisRenderer: yAxis,
      });
      setSeriesState(createdSeries);

      createBaseTooltips({ root, chart, xAxis: xAxis as any, yAxis, theme });
      handleZoom(chart);
      if (download) createDownloadButton(root, xAxisField, data);
      addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

      // Execute callbacks when chart has actually finished rendering and animating
      executeCallbacks(createdSeries);

      return () => {
        setSeriesState(null);
        root.dispose();
      };
    }, [data]);

    return (
      <mui.core.Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <div
          id={widgetId}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </mui.core.Box>
    );
  }
);

export default Chart;
